<template>
  <b-container class="content" fluid>
    <b-row class="align-items-start">
      <template v-if="!searchIsRunning && model.params.done">
        <b-col sm="2" class="px-2 d-none d-sm-block py-3 border rounded bg-light">
          <h4 class="view-name">Filters</h4>
          <div v-if="model.params.done">
            <b-row>
              <b-col sm="12">
                <SortFields
                @sort="onSort"
                @pagination="onPaginate"
                :hotelThemes="hotelThemes"
                :hotelTypes="hotelTypes"
                :hotelAmenities="hotelAmenities"
                :roomAmenities="roomAmenities"
                :priceFilters="this.priceFilters"
                :selectedPriceFilters="this.selectedPriceFilters"
                :selectedHotelThemes="selectedHotelThemes"
                :selectedHotelTypes="selectedHotelTypes"
                :selectedHotelAmenities="selectedHotelAmenities"
                :selectedRoomAmenities="selectedRoomAmenities"
                @priceFiltersChange="onPriceFiltersChange"
                @hotelThemesChange="onHotelThemesChange"
                @hotelTypesChange="onHotelTypesChange"
                @hotelAmenitiesChange="onHotelAmenitiesChange"
                @roomAmenitiesChange="onRoomAmenitiesChange"
                @filtersReset="onFiltersReset"
                v-if="model.results.length" />
              </b-col>
            </b-row>
          </div>
        </b-col>
      </template>
      <b-col>
        <b-overlay :show="searchIsRunning" rounded="sm">
          <SearchForm
            :params="model.params"
            @search="runSearch"
            @openFees="onOpenFees"
            />
        </b-overlay>
        <template v-if="!searchIsRunning && model.params.done">
          <div class="mt-3 mt-sm-5 result-summary p-2 py-1" no-body v-if="model.params.done">
            <b-row class="align-items-center">
              <b-col md="12" class="mb-2">
                <SearchResultsSummary :params="model.params" results="model.results"/>
              </b-col>
            </b-row>
          </div>
          <Legend v-if="model.results.length" />
          <template v-if="model.results.length">
            <HotelResult
              v-for="(result,resultIndex) in paginatedResults"
              :result="result"
              :key="'result' + resultIndex"
              :selected="isSelected(result)"
              :expanded="resultIndex === 0"
              :displayedMeal="result.categories[0].meals[0]"
              :params="model.params"
              :fees="model.fees"
              @info="onInfo([result])"
              @toggle="onToggle(result)"
              >
                <hr />
                <div class="table-responsive">
                  <table class="table table-sm table-hover">
                    <thead class="t-head thead-light">
                      <td v-translate>Room</td>
                      <td style="width:5px">&nbsp;</td>
                      <td v-translate>Meal</td>
                      <td v-translate>Booking price</td>
                      <td v-translate>Sale</td>
                      <td></td>
                      <td v-translate>Offer type</td>
                      <td>&nbsp;</td>
                    </thead>
                    <tbody>
                      <CategoryResult
                        v-for="(category, catIndex) in result.categories"
                        :category="category"
                        :currency="model.params.currency"
                        :key="'cat' + catIndex"
                        :fees="model.fees"
                        :resultIndex="resultIndex"
                        :starRating="result.stars"
                        @book="onBook({ resultId: result.id, categoryId: category.id, ...$event })"
                        @priceDetails="onPriceDetails({ result, category, ...$event })"
                        ></CategoryResult>
                    </tbody>
                  </table>
                </div>
              </HotelResult>
              <b-pagination
                v-model="currentPage"
                :totalRows="model.results.length"
                :per-page="resultsPerPage"
                @change="scrollToTop()"
                align="center"
                class="mt-4"
                />
          </template>
        </template>
      </b-col>
    </b-row>
    <b-modal v-model="feesModal" size="lg" hide-header hide-footer>
      <SimpleModalHeader  @closeModal="onCloseFeesModal" />
      <ServiceTax :fees="model.fees" />
      <ModalFooter @closeModal="onCloseFeesModal" />
    </b-modal>
    <SelectedResults
      :results="selectedResults"
      v-show="selectedResults.length"
      @hide="onHideSelected()"
      @info="onInfoSelected()"
      />
    <b-modal v-model="modal" size="xl" hide-header hide-footer>
      <div id="printArea">
        <ModalHeader @print="onPrint" @download="onDownload" @closeModal="onModalClose" />
        <SearchParamsSummary :params="model.params"/>
        <b-row v-for="result in infoResults" :key="'detailedInfo' + result.id" class="p-4">
          <DetailedInfo
            :result="result"
            :currency="model.params.currency"
            :fees="model.fees"
          ></DetailedInfo>
        </b-row>
      </div>
      <ModalFooter @closeModal="onModalClose" />
    </b-modal>

    <b-modal v-model="priceDetailsModal" :title="$gettext('Price Details')" size="lg" hide-header hide-footer>
      <SimpleModalHeader @closeModal="closePriceDetailsModal" />
      <PriceDetails
        :title="priceDetailsParams.title"
        :meal="priceDetailsParams.meal"
        :serviceTax="priceDetailsParams.serviceTax"
        :discounts="priceDetailsParams.discounts"
        :paymentSchedule="priceDetailsParams.paymentSchedule"
      />
      <ModalFooter @closeModal="closePriceDetailsModal" />
    </b-modal>
  </b-container>
</template>

<script>
import { getServiceFee } from 'b2b-common/utils/hotel'
import model from './services/model'

import viewMixin from '../shared/mixins/search_views'
import SearchForm from './components/search_form'
import SearchResultsSummary from './components/search_results_summary'
import SearchParamsSummary from './components/search_params_summary'
import HotelResult from '@/app/private/shared/components/hotel_result'
import CategoryResult from '@/app/private/shared/components/category_result'
import SimpleModalHeader from '../shared/components/simple_modal_header'
import ModalHeader from '../shared/components/modal_header'
import ModalFooter from '../shared/components/modal_footer'
import ServiceTax from './components/service_tax'
import DetailedInfo from './components/detailed_info'
import SelectedResults from '@/app/private/shared/components/selected_results'
import PriceDetails from '@/app/private/shared/components/price_details'
import SortFields from '@/app/private/shared/components/sort_fields'
import Legend from '@/app/private/shared/components/legend'

import { paymentSchedule } from 'b2b-common/services/api/hotel'

export default {
  components: {
    SearchForm,
    SearchResultsSummary,
    SearchParamsSummary,
    HotelResult,
    CategoryResult,
    ModalHeader,
    ModalFooter,
    SimpleModalHeader,
    ServiceTax,
    SelectedResults,
    DetailedInfo,
    PriceDetails,
    SortFields,
    Legend
  },
  mixins: [viewMixin],
  data () {
    // const priceModifier = model.params.currency + '/pp/pn'
    const priceFilters = [
      { text: this.$gettext('1 - 100 EUR pppn'), value: '0-100', minVal: 1, maxVal: 100 },
      { text: this.$gettext('101 - 200 EUR pppn'), value: '101-200', minVal: 101, maxVal: 200 },
      { text: this.$gettext('201 - 300 EUR pppn'), value: '201-300', minVal: 201, maxVal: 300 },
      { text: this.$gettext('301 - 400 EUR pppn'), value: '301-400', minVal: 301, maxVal: 400 },
      { text: this.$gettext('401 - 500 EUR pppn'), value: '401-500', minVal: 401, maxVal: 500 },
      { text: this.$gettext('over 500 EUR pppn'), value: '501-', minVal: 501, maxVal: Number.MAX_SAFE_INTEGER }
    ]
    return {
      model,
      feesModal: false,
      priceDetailsParams: {
        meal: null,
        title: '',
        discounts: [],
        serviceTax: 0
      },
      priceFilters,
      selectedPriceFilters: [],
      selectedHotelThemes: [],
      selectedHotelTypes: [],
      selectedHotelAmenities: [],
      selectedRoomAmenities: []
    }
  },
  computed: {
    paginatedResults () {
      let results = this.model.results
      if (this.filteredResults) results = this.filteredResults
      return results.slice((this.currentPage - 1) * this.resultsPerPage, this.currentPage * this.resultsPerPage)
    },
    filteredResults () {
      const noNights = this.model.getDuration()
      const noPax = this.model.getNoPax()

      let filtered = [...this.model.results]

      // no filters and all filters should work the same (at least that's what I think now)
      if (this.selectedPriceFilters.length && (this.selectedPriceFilters.length < this.priceFilters.length)) {
        // this might become too slow, so we might need to move this to the model
        // of actutally using array.some to check if any elements in the various arrays match the condition
        // but keep all results with matching elements

        // apply price filters
        filtered = filtered
          // map results to filter out any categories that don't match the price filters
          .map(el => ({
            ...el,
            categories: el.categories
              .map(el2 => ({
                ...el2,
                meals: el2.meals.filter(el3 => {
                  const price = (el3.price + el3.tax) / noNights / noPax
                  const filterIndex = this.selectedPriceFilters.findIndex(el4 => {
                    const filterValue = this.priceFilters.find(el5 => el5.value === el4)
                    return (price >= filterValue.minVal) && (price <= filterValue.maxVal)
                  })
                  return filterIndex >= 0
                })
              }))
              // and then filter out any categories that don't have any meals left
              .filter(el2 => el2.meals.length)
          }))
          // and then filter out any packages that don't have any categories left
          .filter(el1 => el1.categories.length)
      }

      if (this.confirmedOnly) {
        filtered = filtered.filter(el =>
          el.categories.some(el2 => el2.hotelIsAvailable)
        )
      }

      if (this.freeChildOnly) {
        filtered = filtered.filter(el =>
          el.categories.some(el2 => el2.isFreePax)
        )
      }

      if (this.selectedHotelTypes.length && (this.selectedHotelTypes.length < this.model.hotelTypes.length)) {
        filtered = filtered.filter(el => this.selectedHotelTypes.includes(el.property_type))
      }

      if (this.selectedHotelThemes.length && (this.selectedHotelThemes.length < this.model.hotelThemes.length)) {
        filtered = filtered.filter(
          el => this.selectedHotelThemes.some(
            el2 => el.hotel_themes?.some(el3 => el3.id === el2) ?? false
          )
        )
      }

      if (this.selectedHotelAmenities.length) {
        // need to filter out any results that don't have all hotel amenities
        // selectedHotelAmenities is an array of ids
        // hotel_amenities is an array of objects with id and name
        // so we need to filter out any results that don't have all the ids in selectedHotelAmenities

        filtered = filtered.filter(el => {
          const hotelAmenities = el.hotel_amenities.map(el2 => el2.id)
          return this.selectedHotelAmenities.every(el2 => hotelAmenities.includes(el2))
        })
      }

      if (this.selectedRoomAmenities.length) {
        // same principle as hotel amenities
        filtered = filtered.filter(el => {
          const roomAmenities = el.room_amenities.map(el2 => el2.id)
          return this.selectedRoomAmenities.every(el2 => roomAmenities.includes(el2))
        })
      }

      return filtered
    },
    hotelAmenities () {
      return this.model.hotelAmenities.filter(el => {
        return this.model.results.findIndex(result => result.hotel_amenities.some(el2 => el2.id === el.id)) !== -1
      })
    },
    roomAmenities () {
      return this.model.roomAmenities.filter(el => {
        return this.model.results.findIndex(result => result.room_amenities.some(el2 => el2.id === el.id)) !== -1
      })
    },
    hotelThemes () {
      return this.model.hotelThemes.filter(el => {
        return this.model.results.findIndex(result => result.hotel_themes.some(el2 => el2.id === el.id)) !== -1
      })
    },
    hotelTypes () {
      return this.model.hotelTypes.filter(el => {
        return this.model.results.findIndex(result => result.property_type === el) !== -1
      })
    }
  },
  methods: {
    runSearch (params) {
      this.searchIsRunning = true
      model.search(params)
        .finally(() => { this.searchIsRunning = false })
    },
    onOpenFees () {
      this.feesModal = true
    },
    onCloseFeesModal () {
      model.saveFees()
      this.feesModal = false
    },
    onBook ({ resultId, categoryId, mealIndex }) {
      const route = {
        name: 'Private/Hotel/Book',
        params: {
          Result: resultId,
          Category: categoryId,
          Meal: mealIndex
        }
      }
      this.$router.push(route)
    },
    async onPriceDetails ({ result, category, meal }) {
      this.priceDetailsParams.title = result.name
      this.priceDetailsParams.meal = meal
      this.priceDetailsParams.discounts = []
      if (Array.isArray(category.discounts)) this.priceDetailsParams.discounts = category.discounts
      this.priceDetailsParams.serviceTax = getServiceFee({
        meal: meal,
        starRating: result.stars,
        noRooms: this.model.params.rooms.length,
        fees: this.model.fees
      })
      this.priceDetailsParams.paymentSchedule = await paymentSchedule({
        source: result.source,
        category: category.id,
        meal: meal.id
      })
      this.priceDetailsModal = true
    },
    onPriceFiltersChange (val) {
      // remove all elements form selectedPriceFilters that are not in val
      this.selectedPriceFilters.splice(0, this.selectedPriceFilters.length, ...val)
    },
    onHotelAmenitiesChange (val) {
      this.selectedHotelAmenities.splice(0, this.selectedHotelAmenities.length, ...val)
    },
    onRoomAmenitiesChange (val) {
      this.selectedRoomAmenities.splice(0, this.selectedRoomAmenities.length, ...val)
    },
    onHotelThemesChange (val) {
      this.selectedHotelThemes.splice(0, this.selectedHotelThemes.length, ...val)
    },
    onHotelTypesChange (val) {
      this.selectedHotelTypes.splice(0, this.selectedHotelTypes.length, ...val)
    },
    onFiltersReset () {
      this.selectedHotelThemes.splice(0, this.selectedHotelThemes.length)
      this.selectedHotelTypes.splice(0, this.selectedHotelTypes.length)
      this.selectedHotelAmenities.splice(0, this.selectedHotelAmenities.length)
      this.selectedRoomAmenities.splice(0, this.selectedRoomAmenities.length)
      this.selectedPriceFilters.splice(0, this.selectedPriceFilters.length)
      this.confirmedOnly = false
      this.freeChildOnly = false
    }
  }
}
</script>
